import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "react-query";
import {
  getEmailData,
  getFileData,
  getDuplicateFiles,
  getDatabaseTableData,
  getConversionData,
  initiateFileRescan,
  initiateDbRescan,
  getDataSettings,
  updateUserClassification,
  updateUserClass,
  getInstalledPluginList,
  getGenericDetails,
} from "@services/api/ddrCommonApis";
import { getDuplicateFiles as getDuplicateFileIds } from "@services/api/datainventory";
import { Table, TableBody, TableCell, TableRow } from "@components/ui/table";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@components/ui/drawer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@components/ui/tabs";
import DataTable from "@components/common/DataTable";
import {
  X,
  Check,
  Loader2,
  InfoIcon,
  CircleArrowLeft,
  CircleX,
  EditIcon,
  LoaderIcon,
} from "lucide-react";
import { Separator } from "@components/ui/separator";
import SiloImage from "@components/common/ddr/SiloImage";
import { EpochToDateTime, formatBandwidth } from "@utils/utils";
import {
  conversationProp,
  databaseProp,
  DuplicateFilesProp,
  EmailProp,
  FileProp,
} from "./ddrTypes";
import { SearchLoader } from "@components/loaders/S360Loaders";
import { Button } from "../../ui/button";
import { toast } from "../../ui/use-toast";
// import DDRUserInvestigator from "./user/DDRUserInvestigator";
import CustomTooltip from "../CustomTooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import PluginActionDialog from "../../../pages/homepage/aml-anomaly-detection/PluginActionDialog";
import UserInvestigator from "../UserInvestigator";
import { isFeatureAllowed, ALL_KEYS } from "@utils/licenseUtils";

interface FileInvestigatorProps {
  Id: number;
  isOpen?: boolean;
  onClose?: () => void;
  direct_file?: boolean;
  type?: "file" | "email" | "database" | "conversation" | "generic" | ""; //file,email or db
}
const FileInvestigator: React.FC<FileInvestigatorProps> = ({
  Id: uniqueId,
  isOpen = false,
  onClose,
  direct_file = false,
  type: kind = "file", //"email"|"database"
}) => {
  const [isExternal, setIsExternal] = useState<boolean>(false);
  const [pluginAction, setPluginAction] = useState<boolean>(false);
  const [Id, setId] = useState(uniqueId);
  const [type, setType] = useState(direct_file ? "file" : kind); // "file" | "email" | "database" | "conversation"
  const [source, setSource] = useState(direct_file ? "file" : kind);
  const [_duplicateIds, setDuplicateIds] = useState<number[]>([]);
  const [duplicateFiles, setDuplicateFiles] = useState<any[] | null>(null);
  const [editDropdown, setEditDropdown] = useState<string>("");
  const [savingClassification, setSavingClassification] =
    useState<boolean>(false);
  const [savingTaxonomy, setSavingTaxonomy] = useState<boolean>(false);
  const [rescanLoading, setRescanLoading] = useState<boolean>(false);

  useEffect(() => {
    setId(uniqueId);
    setType(direct_file ? "file" : kind);
    setSource(direct_file ? "file" : kind);
    setDuplicateIds([]);
    setDuplicateFiles(null);
  }, [uniqueId, kind, direct_file, isOpen]);

  if (!isOpen || type == "") {
    return null;
  }

  const { data: installedPluginList } = useQuery(
    ["getInstalledPluginList"],
    ({ signal }) => getInstalledPluginList(signal),
    { enabled: isOpen },
  );

  const getPluginName = (plugin_id: string) => {
    const plugin = installedPluginList?.find(
      (item: any) => item.id === plugin_id,
    );
    if (plugin) {
      return plugin.name;
    } else {
      return "";
    }
  };

  // --------------------File---------------------------------------------------------------------
  const {
    data: _fileData,
    isLoading,
    refetch,
    isRefetching,
  } = useQuery<FileProp>(["file", Id], () => getFileData(Id, source), {
    enabled: !!Id && (type == "file" || direct_file),
  });

  const [fileData, setFileData] = useState<FileProp | null>(null);

  useEffect(() => {
    if (_fileData) {
      // const temp: any = {
      //   ..._fileData,
      //   path: _fileData.path ? _fileData.silo + "/" + _fileData.path : "--",
      // };
      // setFileData(temp);
      setFileData(_fileData);
      if (_fileData.external_classification) {
        const externalClassification = _fileData.external_classification.map(
          (item: any) => {
            return {
              source: item.source,
              classification: item.external_label_name,
              lsClassification: item.external_taxonomy,
            };
          },
        );
        setExternalClassification(externalClassification);
      }
    }
  }, [_fileData]);

  const {
    data: _duplicateFiles,
    isLoading: _duplicateFilesLoading,
    isFetching,
    dataUpdatedAt,
  } = useQuery<DuplicateFilesProp>(
    ["duplicateFileData", Id, source],
    ({ signal }) =>
      getDuplicateFileIds(signal, {
        limit: 10,
        page: 0,
        file_id: Id,
        source: source,
      }),
    { enabled: !!Id && type == "file" },
  );

  useEffect(() => {
    if (Id === undefined || Id === null || type != "file") {
      setDuplicateIds([]);
      setDuplicateFiles(null);
    }
  }, [Id, type, source]);

  useEffect(() => {
    if (_duplicateFiles) {
      let duplicate_Ids: number[] = [];
      if (
        _duplicateFiles?.data &&
        Object.keys(_duplicateFiles.data).length > 0
      ) {
        for (const key in _duplicateFiles.data) {
          const duplicateFilesArray =
            _duplicateFiles.data[key]?.duplicate_files;
          if (Array.isArray(duplicateFilesArray)) {
            duplicate_Ids = [...duplicate_Ids, ...duplicateFilesArray];
          }
        }
      }
      setDuplicateIds(duplicate_Ids);
    } else {
      setDuplicateIds([]);
    }
  }, [_duplicateFiles, dataUpdatedAt]);

  const { data: duplicateData, isLoading: duplicatesLoading } = useQuery<
    FileProp[]
  >(
    ["getDuplicateFileDetails", source, _duplicateIds],
    ({ signal }) =>
      getDuplicateFiles({ file_ids: _duplicateIds, source: source }, signal),
    {
      enabled: type == "file" && _duplicateIds.length > 0,
    },
  );

  useEffect(() => {
    const data =
      duplicateData?.map((file: FileProp) => {
        return {
          ...file,
          service: (
            <CustomTooltip text={file.service} side="left">
              <div>
                <SiloImage
                  type={file.service}
                  variant="image"
                  className="h-6"
                />
              </div>
            </CustomTooltip>
          ),
          size: <div className="min-w-14">{formatBandwidth(file.size)}</div>,
        };
      }) || [];
    setDuplicateFiles(data);
  }, [duplicateData]);

  const userData =
    fileData?.users?.map((user) => {
      const plugin_name = getPluginName(user?.plugin_id || "");
      return {
        ...user,
        source: user.source
          ? `${user.source} - ${plugin_name}`
          : plugin_name
            ? plugin_name
            : "--",
        create: user.create ? <Check size={14} /> : <X size={14} />,
        read: user.read ? <Check size={14} /> : <X size={14} />,
        update: user.update ? <Check size={14} /> : <X size={14} />,
        delete: user.delete ? <Check size={14} /> : <X size={14} />,
        share: user.share ? <Check size={14} /> : <X size={14} />,
        account_disabled_status: user.account_disabled_status
          ? user.account_disabled_status == "1"
            ? "Yes"
            : user.account_disabled_status == "0"
              ? "No"
              : ""
          : "",
      };
    }) || [];

  const userCols = [
    { header: "Username", accessor: "username" },
    { header: "Create", accessor: "create", type: "component" },
    { header: "Read", accessor: "read", type: "component" },
    { header: "Update", accessor: "update", type: "component" },
    { header: "Delete", accessor: "delete", type: "component" },
    { header: "Share", accessor: "share", type: "component" },
    { header: "Source", accessor: "source" },
    { header: "External User", accessor: "is_external" },
    { header: "Disabled User", accessor: "account_disabled_status" },
  ];

  const duplicateDataCols = [
    { header: "Name", accessor: "name" },
    { header: "Source", accessor: "service", type: "component" },
    { header: "File Size", accessor: "size", type: "component" },
    { header: "File Path", accessor: "path" },
    { header: "Classification", accessor: "classification" },
    { header: "Type", accessor: "extension" },
  ];

  const groups = fileData?.groups?.map((group) => {
    return {
      group_id: group.group_id,
      create: group.create ? <Check size={14} /> : <X size={14} />,
      read: group.read ? <Check size={14} /> : <X size={14} />,
      update: group.update ? <Check size={14} /> : <X size={14} />,
      delete: group.delete ? <Check size={14} /> : <X size={14} />,
      share: group.share ? <Check size={14} /> : <X size={14} />,
    };
  });

  const groupCols = [
    { header: "Group Name", accessor: "group_id" },
    { header: "Create", accessor: "create", type: "component" },
    { header: "Read", accessor: "read", type: "component" },
    { header: "Update", accessor: "update", type: "component" },
    { header: "Delete", accessor: "delete", type: "component" },
    { header: "Share", accessor: "share", type: "component" },
  ];

  const [externalClassification, setExternalClassification] = useState<
    { source: string; classification: string }[]
  >([]);

  // --------------------EMAIL-------------------------------------------------------------------------
  type EmailShowProp = {
    sender?: string;
    recipient?: string;
    cc?: string;
    bcc?: string;
    subject?: string;
    body?: string;
    server_Name: string;
    sensitivity?: string;
    sub_Sensitivity?: string;
  };
  type AttachmentProp = {
    attachmentName: string;
    attachmentType: string;
    attachmentSize: string;
    sensitivity?: string;
    pci_sub_sensitivity: string[];
    phi_sub_sensitivity: string[];
    pii_sub_sensitivity: string[];
  };
  const [email_Data, setEmailData] = useState<EmailShowProp | null>(null);
  const [attachmentData, setAttachmentData] = useState<AttachmentProp[] | null>(
    null,
  );

  const { data: emailData, isLoading: isLoadingEmail } = useQuery<EmailProp>(
    ["email", Id],
    () => getEmailData(Id),
    {
      enabled: !!Id && type == "email",
    },
  );

  useEffect(() => {
    if (emailData && Object.keys(emailData)[0]) {
      const _emailData = {
        email_id: Object.keys(emailData)[0],
        ...(emailData as Record<string, any>)[Object.keys(emailData)[0]],
      };

      const uniqueRecipients: any = Array.from(
        new Map(
          (_emailData?.recipients || []).map((item: any) => [
            item.recipient_email,
            item,
          ]),
        ).values(),
      );

      const temp = {
        sender: _emailData?.sender_name + " (" + _emailData?.sender_email + ")",
        recipient: uniqueRecipients
          .filter(
            (rec: { recipient_type: string }) => rec.recipient_type === "to",
          )
          .map(
            (rec: { recipient_name: string; recipient_email: string }) =>
              `${rec.recipient_name} (${rec.recipient_email})`,
          )
          .join(", "),
        cc: uniqueRecipients
          .filter(
            (rec: { recipient_type: string }) => rec.recipient_type === "cc",
          )
          .map(
            (rec: { recipient_name: string; recipient_email: string }) =>
              `${rec.recipient_name} (${rec.recipient_email})`,
          )
          .join(", "),
        bcc: uniqueRecipients
          .filter(
            (rec: { recipient_type: string }) => rec.recipient_type === "bcc",
          )
          .map(
            (rec: { recipient_name: string; recipient_email: string }) =>
              `${rec.recipient_name} (${rec.recipient_email})`,
          )
          .join(", "),
        subject: _emailData?.subject,
        externally_shared: _emailData?.externally_shared ?? "--",
        // body: _emailData?.body,
        sensitivity: _emailData?.sensitivity ?? "--",
        server_Name: _emailData?.server_name || "",
        sub_Sensitivity: [
          ..._emailData.pci_sub_sensitivity,
          ..._emailData.phi_sub_sensitivity,
          ..._emailData.pii_sub_sensitivity,
        ].join(", "),
        classification: _emailData?.classification ?? "--",
        taxonomy: _emailData?.taxonomy ?? "--",
      };
      setEmailData(temp);

      const temp1 = _emailData?.attachments.map((att: any) => {
        return {
          attachmentId: att.attachment_id,
          attachmentName: att.attachment_name,
          attachmentType: att.attachment_type,
          attachmentSize: formatBandwidth(att.attachment_size),
          sensitivity: att.sensitivity,
          subSensitivity: [
            ...att.pci_sub_sensitivity,
            ...att.phi_sub_sensitivity,
            ...att.pii_sub_sensitivity,
          ].join(", "),
        };
      });

      if (temp1) {
        setAttachmentData(temp1);
      }
    }
  }, [emailData]);

  const attachmentCols = [
    { header: "Name", accessor: "attachmentName" },
    { header: "Type", accessor: "attachmentType" },
    { header: "Size", accessor: "attachmentSize" },
    { header: "Sensitivity", accessor: "sensitivity" },
    { header: "Sub Sensitivity", accessor: "subSensitivity" },
  ];

  // ----------------------Database-------------------------------------------------------------------------------

  const { data: _tableDetails, isLoading: isLoadingDatabase } =
    useQuery<databaseProp>(
      ["database", Id],
      ({ signal }) => getDatabaseTableData(Id, signal),
      {
        enabled: !!Id && type == "database",
      },
    );

  const [tableDetails, setTableDetails] = useState<databaseProp | null>(null);

  useEffect(() => {
    if (_tableDetails) {
      const temp: any = {
        ..._tableDetails,
        path: _tableDetails.path
          ? _tableDetails.source_name + "/" + _tableDetails.path
          : "--",
      };
      setTableDetails(temp);
    }
  }, [_tableDetails]);

  const dbUserData =
    tableDetails?.users?.map((user) => {
      const plugin_name = getPluginName(user?.plugin_id || "");
      return {
        ...user,
        source: user.source
          ? `${user.source} - ${plugin_name}`
          : plugin_name
            ? plugin_name
            : "--",
        create: user.create ? <Check size={14} /> : <X size={14} />,
        read: user.read ? <Check size={14} /> : <X size={14} />,
        update: user.update ? <Check size={14} /> : <X size={14} />,
        delete: user.delete ? <Check size={14} /> : <X size={14} />,
        share: user.share ? <Check size={14} /> : <X size={14} />,
        account_disabled_status: user.account_disabled_status
          ? user.account_disabled_status == "1"
            ? "Yes"
            : user.account_disabled_status == "0"
              ? "No"
              : ""
          : "",
      };
    }) || [];

  const dbUserCols = [
    { header: "Username", accessor: "username" },
    { header: "Create", accessor: "create", type: "component" },
    { header: "Read", accessor: "read", type: "component" },
    { header: "Update", accessor: "update", type: "component" },
    { header: "Delete", accessor: "delete", type: "component" },
    { header: "Share", accessor: "share", type: "component" },
    { header: "Source", accessor: "source" },
    { header: "Disabled User", accessor: "account_disabled_status" },
  ];

  // -------------------conversation------------------------------------------------------

  type conversationShowProp = {
    sender?: string;
    recipient?: string;
    subject?: string;
    server_Name: string;
    sensitivity?: string;
    sub_Sensitivity?: string;
  };
  const [conversationDetails, setConversationDetails] =
    useState<conversationShowProp | null>(null);
  const [_attachmentData, _setAttachmentData] = useState<
    AttachmentProp[] | null
  >(null);

  const { data: conversationData, isLoading: isLoadingConversation } =
    useQuery<conversationProp>(
      ["conversation", Id],
      () => getConversionData(Id),
      {
        enabled: !!Id && type == "conversation",
      },
    );

  useEffect(() => {
    if (conversationData && Object.keys(conversationData)[0]) {
      const _conversationData = {
        conversation_id: Object.keys(conversationData)[0],
        ...(conversationData as Record<string, any>)[
          Object.keys(conversationData)[0]
        ],
      };

      const temp = {
        sender:
          _conversationData?.sender_name +
          " (" +
          _conversationData?.sender_email +
          ")",
        recipient: _conversationData?.recipients
          .map(
            (rec: { recipient_name: string; recipient_email: string }) =>
              `${rec.recipient_name} (${rec.recipient_email})`,
          )
          .join(", "),
        subject: _conversationData?.subject,
        externally_shared: _conversationData?.externally_shared ?? "--",
        sent_at: EpochToDateTime(_conversationData?.sent_at),
        sensitivity: _conversationData?.sensitivity,
        server_Name: _conversationData?.server_name || "",
        sub_Sensitivity: [
          ..._conversationData.pci_sub_sensitivity,
          ..._conversationData.phi_sub_sensitivity,
          ..._conversationData.pii_sub_sensitivity,
        ].join(", "),
        classification: _conversationData?.classification ?? "--",
        taxonomy: _conversationData?.taxonomy ?? "--",
      };
      setConversationDetails(temp);

      const temp1 = _conversationData?.attachments.map((att: any) => {
        return {
          attachmentId: att.attachment_id,
          attachmentName: att.attachment_name,
          attachmentType: att.attachment_type,
          attachmentSize: formatBandwidth(att.attachment_size),
          sensitivity: att.sensitivity,
          subSensitivity: [
            ...att.pci_sub_sensitivity,
            ...att.phi_sub_sensitivity,
            ...att.pii_sub_sensitivity,
          ].join(", "),
        };
      });

      if (temp1) {
        _setAttachmentData(temp1);
      }
    }
  }, [conversationData]);

  const ConversationAttachmentCols = [
    { header: "Name", accessor: "attachmentName" },
    { header: "Type", accessor: "attachmentType" },
    { header: "Size", accessor: "attachmentSize" },
    { header: "Sensitivity", accessor: "sensitivity" },
    { header: "Sub Sensitivity", accessor: "subSensitivity" },
  ];

  // ---------------Generic----------------------------------------------------------------

  const [genericDetails, setGenericDetails] = useState<any | null>(null);
  const [genericAttachmentData, setGenericAttachmentData] = useState<any[]>([]);

  const { data: genericData, isLoading: isLoadingGeneric } = useQuery(
    ["generic", Id],
    ({ signal }) =>
      getGenericDetails(
        {
          generic_id: Id.toString(),
          product: "",
        },
        signal,
      ),
    {
      enabled: !!Id && type == "generic",
    },
  );

  React.useEffect(() => {
    if (genericData && Object.keys(genericData)[0]) {
      const _genericData = {
        generic_id: Object.keys(genericData)[0],
        ...(genericData as Record<string, any>)[Object.keys(genericData)[0]],
      };
      const temp = {
        user_name: _genericData.user_name,
        product_name: _genericData.product_name,
        classification: _genericData.classification,
        sensitivity: _genericData.sensitivity,
        sub_sensitivity: [
          ...(_genericData.pci_sub_sensitivity || []),
          ...(_genericData.phi_sub_sensitivity || []),
          ...(_genericData.pii_sub_sensitivity || []),
        ].join(", "),
        category: _genericData.category,
        taxonomy: _genericData.taxonomy,
        sent_at: _genericData.sent_at
          ? EpochToDateTime(_genericData.sent_at)
          : "--",
        is_external_user: _genericData.is_external_user,
      };
      setGenericDetails(temp);

      const temp1 = _genericData.attachments.map((att: any) => {
        return {
          attachmentId: att.attachment_id,
          name: att.attachment_name,
          type: att.attachment_type,
          size: formatBandwidth(att.attachment_size),
          sensitivity: att.sensitivity,
          sub_sensitivity: [
            ...att.pci_sub_sensitivity,
            ...att.phi_sub_sensitivity,
            ...att.pii_sub_sensitivity,
          ].join(", "),
        };
      });
      setGenericAttachmentData(temp1);
    }
  }, [genericData]);

  const genericAttachmentCols = [
    { header: "Name", accessor: "name" },
    { header: "Type", accessor: "type" },
    { header: "Size", accessor: "size" },
    { header: "Sensitivity", accessor: "sensitivity" },
    { header: "Sub Sensitivity", accessor: "sub_sensitivity" },
  ];

  // ----------------Rescan---------------------------------------------------------------------

  const rescanMutation = useMutation(
    (data: RescanInput) => {
      if (type === "file") {
        return initiateFileRescan(data);
      } else {
        return initiateDbRescan(data);
      }
    },
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Rescan initiated successfully",
          variant: "success",
        });
        setRescanLoading(false);
      },
      onError: (error) => {
        console.log("rescanInfo", error);
        toast({
          title: "Error",
          description: "Failed to initiate rescan",
          variant: "destructive",
        });
        setRescanLoading(false);
      },
    },
  );

  type RescanInput = {
    file_id?: number;
    silo?: string;
    service?: string;
    table_id?: number;
    db_name?: string;
  };

  const setRescan = () => {
    setRescanLoading(true);
    if (type == "file") {
      rescanMutation.mutate({
        file_id: Id,
      });
    } else if (type == "database") {
      rescanMutation.mutate({
        table_id: Id,
      });
    }
  };

  const [_user, setUser] = useState<string>("");
  const [pluginId, setPluginId] = useState<string>("");

  // -------------change taxonomy-------------------------------
  const [taxonomyList, setTaxonomyList] = useState<any[]>([]);

  const { data: dataSettings, isLoading: settingsLoading } = useQuery(
    ["getDataSettings"],
    ({ signal }) => getDataSettings(signal),
  );

  useEffect(() => {
    if (dataSettings && dataSettings.taxonomy) {
      const temp: any[] = [];
      dataSettings.taxonomy.map((item: any) => {
        temp.push({ value: item.title, label: item.title });
      });
      setTaxonomyList(temp);
    }
  }, [dataSettings]);

  const updateClassificationMutation = useMutation(updateUserClassification, {
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Classification updated successfully",
        variant: "success",
      });
      setEditDropdown("");
      refetch();
      setSavingTaxonomy(false);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update classification",
        variant: "destructive",
      });
      setSavingTaxonomy(false);
    },
  });

  const updateClassMutation = useMutation(updateUserClass, {
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Classification updated successfully",
        variant: "success",
      });
      setEditDropdown("");
      refetch();
      setSavingClassification(false);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update classification",
        variant: "destructive",
      });
      setSavingClassification(false);
    },
  });

  const onChnageHandler = (val: any, actual_val: any) => {
    setSavingTaxonomy(true);
    if (val && val != actual_val) {
      const temp_source =
        source == "email"
          ? "email_attachment"
          : source == "conversation"
            ? "conversation_attachment"
            : source == "generic"
              ? "generic_attachment"
              : source;
      const data_to_send = {
        type: temp_source,
        id: Id,
        user_classification: val,
      };
      updateClassificationMutation.mutate(data_to_send);
    }
  };

  const changeClassification = (val: any, actual_val: any) => {
    setSavingClassification(true);
    if (val && val != actual_val) {
      const data_to_send = {
        file_id: Id,
        user_classification: val,
        source: source,
      };
      updateClassMutation.mutate(data_to_send);
    }
  };

  const handleDuplicateClick = (row: any) => {
    setId(row.id);
    setType("file");
    setSource(row.source);
  };

  const IdArray = useRef<any[]>([]);

  useEffect(() => {
    if (Id && type) {
      const temp = {
        Id: Id,
        type: type,
        source: source,
      };
      IdArray.current = IdArray.current.filter(
        (item) =>
          item.Id !== Id || item.type !== type || item.source !== source,
      );
      IdArray.current.push(temp);
    }
  }, [Id, type]);

  const handlePev = () => {
    IdArray.current.pop();
    setId(IdArray.current[IdArray.current.length - 1].Id);
    setType(IdArray.current[IdArray.current.length - 1].type);
    setSource(IdArray.current[IdArray.current.length - 1].source);
  };

  function openPluginActionDialog() {
    setPluginAction(true);
  }

  const getDefaultTab = () => {
    if (source === "file") return "users";

    if (fileData?.groups && fileData.groups.length > 0) return "groups";

    if (!_duplicateFilesLoading && !isFetching && _duplicateIds?.length > 0)
      return "duplicates";

    if (externalClassification?.length > 0) return "external_classification";

    return undefined;
  };

  // ToDO Holding this section for now

  const generateS360Url = () => {
    const logType = "event_logs";

    const queryObj = {
      common_rules: [],
      rule_aggregate: [],
      rules: [
        {
          id: 1,
          rule_id: 100,
          log_source: logType,
          field_name: "EventData",
          values: "_dynamic",
          external_conditions_set: [
            {
              method: "in_reverse",
              args: {
                value: fileData?.silo,
                _method: "",
              },
              result: true,
            },
          ],
          external_operation_set: [],
        },
        {
          id: 2,
          rule_id: 100,
          log_source: logType,
          field_name: "EventData",
          values: "_dynamic",
          external_conditions_set: [
            {
              method: "in_reverse",
              args: {
                value: fileData?.name,
                _method: "",
              },
              result: true,
            },
          ],
          external_operation_set: [],
        },
        {
          id: 3,
          rule_id: 100,
          log_source: logType,
          field_name: "EventData",
          values: "_dynamic",
          external_conditions_set: [
            {
              method: "in_reverse",
              args: {
                value: "Lineage",
                _method: "",
              },
              result: true,
            },
          ],
          external_operation_set: [],
        },
      ],
      group: [
        {
          aggregate_rules: [],
          base_rule_joins: ["1", "AND", "2", "AND", "3"],
          common_rule_join: [],
          id: 1,
          log_source: logType,
        },
      ],
      group_join: ["1"],
      log_source: [logType],
    };

    const params = new URLSearchParams({
      date: JSON.stringify({
        startDate: fileData?.created_at,
        endDate: Math.floor(Date.now() / 1000),
      }),
      plainText: "null",
      smartSearch: "0",
      query: JSON.stringify(queryObj),
      sensorDetails: "null",
      smartResType: "defined",
      fieldSearch: "false",
      logLimit: "1000",
      sequential: "0",
      isCaseSensitive: "1",
    });

    const s360Url = `/shadow360?${params.toString()}`;

    window.open(s360Url, "_blank");
  };
  return (
    <>
      {_user != "" && (
        // <DDRUserInvestigator
        //   isOpen={true}
        //   onClose={() => {
        //     setUser("");
        //     setPluginId("");
        //     setIsExternal(false);
        //   }}
        //   userId={_user}
        //   is_external={isExternal}
        //   plugin_id={pluginId}
        // />
        <UserInvestigator
          account={_user}
          openState={true}
          onClose={() => {
            setUser("");
            setPluginId("");
            setIsExternal(false);
          }}
          is_external={isExternal}
          pluginId={pluginId}
        />
      )}

      {pluginAction && (
        <PluginActionDialog
          show={true}
          onClose={() => setPluginAction(false)}
          anmlId={0}
          anomalyTime={0}
          anomalyType={`${type}_actions`}
          fileData={_fileData}
        />
      )}

      <Drawer open={isOpen} onOpenChange={onClose} modal>
        <DrawerContent className="mx-auto px-40 min-h-[55vh] overflow-hidden gap-2 scrollbar-thumb-gray-900">
          <div className="max-w-[90vw] min-w-[50vw]">
            {isLoading ||
            isLoadingEmail ||
            isLoadingDatabase ||
            isLoadingConversation ||
            isLoadingGeneric ||
            settingsLoading ||
            isRefetching ? (
              <>
                <SearchLoader show={true} />
                <DrawerHeader>
                  <DrawerTitle></DrawerTitle>
                </DrawerHeader>
              </>
            ) : (
              <>
                <DrawerHeader className="p-0 text-left gap-2 grid grid-cols-12">
                  <div className="flex col-span-10">
                    <div className="flex text-primary items-center">
                      <div className="mr-2">
                        {IdArray && IdArray.current.length > 1 && (
                          <span onClick={handlePev}>
                            <CircleArrowLeft />
                          </span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <DrawerTitle className="text-primary capitalize break-all">
                          {type == "file" && fileData?.name}{" "}
                          {type == "email" && email_Data?.sender}{" "}
                          {type == "database" && tableDetails?.table}{" "}
                          {type == "conversation" &&
                            conversationDetails?.sender}{" "}
                          {type == "generic" && genericDetails?.product_name}{" "}
                        </DrawerTitle>
                        <DrawerDescription className="mt-2">
                          {type.charAt(0).toUpperCase() + type.substring(1)}{" "}
                          details can be seen here
                        </DrawerDescription>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      {(fileData ||
                        email_Data ||
                        tableDetails ||
                        conversationDetails ||
                        genericDetails) && (
                        <div className="flex items-center gap-2">
                          <Separator orientation="vertical" className="mx-6" />
                          {type == "file" && fileData ? (
                            <SiloImage
                              className="h-10"
                              type={fileData.service}
                            />
                          ) : type == "email" && email_Data ? (
                            <SiloImage
                              className="h-10"
                              type={email_Data.server_Name}
                            />
                          ) : type == "database" && tableDetails ? (
                            <SiloImage
                              className="h-10"
                              type={tableDetails.source_name}
                            />
                          ) : type == "conversation" && conversationDetails ? (
                            <SiloImage
                              className="h-10"
                              type={conversationDetails?.server_Name}
                            />
                          ) : type == "generic" && genericDetails ? (
                            <SiloImage
                              className="h-10"
                              type={genericDetails?.product_name}
                            />
                          ) : null}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-4 col-span-2 justify-end">
                    {isFeatureAllowed(
                      ALL_KEYS.FILE_INVESTIGATOR_DATA_LINEAGE,
                    ) &&
                      type == "file" &&
                      source == "file" && (
                        <Button size={"sm"} onClick={() => generateS360Url()}>
                          Data Lineage
                        </Button>
                      )}

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size={"sm"} disabled={rescanLoading}>
                          <span>Actions</span>
                          <LoaderIcon
                            size={14}
                            className={
                              "ms-2 animate-spin " +
                              (rescanLoading ? "" : "hidden")
                            }
                          />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="w-56" align="start">
                        <DropdownMenuGroup>
                          {((type == "file" && source == "file") ||
                            type == "database") && (
                            <DropdownMenuItem
                              onClick={setRescan}
                              className="cursor-pointer hover:bg-secondary"
                            >
                              Rescan
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => openPluginActionDialog()}
                            className="cursor-pointer hover:bg-secondary"
                          >
                            Plugin Action
                          </DropdownMenuItem>
                        </DropdownMenuGroup>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <DrawerClose asChild>
                      <CustomTooltip text="Close" side="bottom">
                        <CircleX onClick={onClose} className="cursor-pointer" />
                      </CustomTooltip>
                    </DrawerClose>
                  </div>
                </DrawerHeader>
                <Separator className="my-2" />
                <div className="overflow-y-auto max-h-[65vh]">
                  {type == "file" && (
                    <div className="mt-4 grid grid-cols-6 gap-x-4">
                      <div className="col-span-2 overflow-y-auto max-h-[60vh]">
                        <Table className="border">
                          <TableBody>
                            {fileData &&
                              (source == "file" || source == "generic"
                                ? [
                                    "name",
                                    "silo",
                                    "path",
                                    "extension",
                                    "created_at",
                                    // "inserted_at",
                                    "accessed_at",
                                    "modified_at",
                                    "updated_at",
                                    "size",
                                    "sensitivity",
                                    "sub_sensitivity",
                                    "service",
                                    "classification",
                                    "is_global",
                                    "is_external",
                                    // "external_classification", // moved to
                                    "taxonomy",
                                  ]
                                : [
                                    "name",
                                    "extension",
                                    "size",
                                    "sensitivity",
                                    "sub_sensitivity",
                                    "service",
                                    "classification",
                                    "is_global",
                                    "is_external",
                                    "taxonomy",
                                  ]
                              ).map((key) => {
                                let val =
                                  fileData[key as keyof typeof fileData];
                                if (
                                  [
                                    // "inserted_at",
                                    "modified_at",
                                    "created_at",
                                    "accessed_at",
                                    "updated_at",
                                  ].includes(key)
                                )
                                  val = fileData[key as keyof typeof fileData]
                                    ? new Date(
                                        (fileData[
                                          key as keyof typeof fileData
                                        ] as number) * 1000,
                                      ).toLocaleString()
                                    : "--";
                                if (key == "size")
                                  val = formatBandwidth(
                                    fileData[
                                      key as keyof typeof fileData
                                    ] as number,
                                    "B",
                                    1,
                                  );
                                return (
                                  <TableRow key={key}>
                                    <TableCell className="p-3 ">
                                      <span className="font-bold capitalize flex">
                                        {key == "silo"
                                          ? "Data Silo"
                                          : key == "updated_at"
                                            ? "Scanned At"
                                            : key.replace("_", " ")}
                                        {key == "modified_at" && (
                                          <CustomTooltip
                                            text={
                                              "On certain cloud platforms, the created_at date may occasionally indicate a later time than the modified_at date due to specific system behaviors or processes."
                                            }
                                            side="right"
                                            className="normal-case"
                                          >
                                            <InfoIcon
                                              size={12}
                                              className="ms-2 text-primary"
                                            />
                                          </CustomTooltip>
                                        )}
                                      </span>
                                    </TableCell>
                                    <TableCell className="p-3">
                                      <div className="flex items-center flex-wrap h-[40px] max-h-[40px] overflow-y-auto overflow-x-hidden break-all">
                                        {key == "taxonomy" ? (
                                          <div className="flex items-center flex-1">
                                            {editDropdown == "taxonomy" ? (
                                              <div className="flex-1 flex gap-2 items-center">
                                                {savingTaxonomy ? (
                                                  <LoaderIcon
                                                    size={14}
                                                    className="ms-2 animate-spin "
                                                  />
                                                ) : (
                                                  <>
                                                    <Select
                                                      onValueChange={(v) =>
                                                        onChnageHandler(v, val)
                                                      }
                                                      value={val as string}
                                                    >
                                                      <SelectTrigger className="flex-1">
                                                        <SelectValue placeholder="Select a taxonomy..." />
                                                      </SelectTrigger>
                                                      <SelectContent>
                                                        {taxonomyList.map(
                                                          (f) => (
                                                            <SelectItem
                                                              key={f.value}
                                                              value={f.value}
                                                            >
                                                              {f.label}
                                                            </SelectItem>
                                                          ),
                                                        )}
                                                      </SelectContent>
                                                    </Select>
                                                    <X
                                                      className="cursor-pointer"
                                                      size={18}
                                                      onClick={() =>
                                                        setEditDropdown("")
                                                      }
                                                    />
                                                  </>
                                                )}
                                              </div>
                                            ) : (
                                              <div className="flex flex-1 items-center">
                                                <div className="flex-1">
                                                  {val || "- - -"}
                                                </div>
                                                <EditIcon
                                                  onClick={() =>
                                                    setEditDropdown("taxonomy")
                                                  }
                                                  size={18}
                                                  className="cursor-pointer"
                                                />
                                              </div>
                                            )}
                                          </div>
                                        ) : key == "classification" ? (
                                          <div className="flex items-center flex-1">
                                            {editDropdown ==
                                            "classification" ? (
                                              <div className="flex-1 flex gap-2 items-center">
                                                {savingClassification ? (
                                                  <LoaderIcon
                                                    size={14}
                                                    className="ms-2 animate-spin "
                                                  />
                                                ) : (
                                                  <>
                                                    <Select
                                                      onValueChange={(v) =>
                                                        changeClassification(
                                                          v,
                                                          val,
                                                        )
                                                      }
                                                      value={val as string}
                                                    >
                                                      <SelectTrigger className="flex-1">
                                                        <SelectValue placeholder="Select classification..." />
                                                      </SelectTrigger>
                                                      <SelectContent>
                                                        {(
                                                          dataSettings?.document_classification_combined ||
                                                          []
                                                        ).map((f: any) => (
                                                          <SelectItem
                                                            key={f.title}
                                                            value={f.title}
                                                          >
                                                            {f.title}
                                                          </SelectItem>
                                                        ))}
                                                      </SelectContent>
                                                    </Select>
                                                    <X
                                                      className="cursor-pointer"
                                                      size={18}
                                                      onClick={() =>
                                                        setEditDropdown("")
                                                      }
                                                    />
                                                  </>
                                                )}
                                              </div>
                                            ) : (
                                              <div className="flex flex-1 items-center">
                                                <div className="flex-1">
                                                  {val || "- - -"}
                                                </div>
                                                <EditIcon
                                                  onClick={() =>
                                                    setEditDropdown(
                                                      "classification",
                                                    )
                                                  }
                                                  size={18}
                                                  className="cursor-pointer"
                                                />
                                              </div>
                                            )}
                                          </div>
                                        ) : (
                                          (val as string)
                                        )}
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                          </TableBody>
                        </Table>
                      </div>
                      <div className="col-span-4">
                        {fileData && (
                          <Tabs defaultValue={getDefaultTab()}>
                            <TabsList className="flex w-[fit-content] justify-start gap-2">
                              {source == "file" && (
                                <TabsTrigger
                                  value="users"
                                  className="font-bold capitalize"
                                >
                                  User Access
                                </TabsTrigger>
                              )}
                              {fileData.groups &&
                                fileData.groups.length > 0 && (
                                  <TabsTrigger
                                    value="groups"
                                    className="font-bold capitalize"
                                  >
                                    Groups
                                  </TabsTrigger>
                                )}
                              {!_duplicateFilesLoading &&
                                !isFetching &&
                                _duplicateIds?.length > 0 && (
                                  <TabsTrigger
                                    value="duplicates"
                                    className="font-bold capitalize"
                                  >
                                    Duplicate Files{" "}
                                    {duplicatesLoading && (
                                      <Loader2
                                        className="ml-2 animate-spin"
                                        size={14}
                                      />
                                    )}
                                  </TabsTrigger>
                                )}
                              {externalClassification &&
                                externalClassification.length > 0 && (
                                  <TabsTrigger
                                    value="external_classification"
                                    className="font-bold capitalize"
                                  >
                                    External Classification
                                  </TabsTrigger>
                                )}
                            </TabsList>
                            <TabsContent value="users">
                              <div className="flex flex-col items-start py-2">
                                {userData && (
                                  <DataTable
                                    data={userData}
                                    sort={false}
                                    // pagination={false}
                                    colFilter={false}
                                    cols={userCols}
                                    search={false}
                                    rowsPerPage={4}
                                    totalCount={userData.length}
                                    className="mt-4 [&_td]:text-sm"
                                    // rowClickColumn="username"
                                    onRowClick={(e) => {
                                      setUser(e.username);
                                      setPluginId(e.plugin_id ?? "");
                                      if (e && e.is_external == "No") {
                                        setIsExternal(false);
                                      } else setIsExternal(true);
                                    }}
                                  />
                                )}
                              </div>
                            </TabsContent>
                            {groups && groups.length > 0 && (
                              <TabsContent value="groups">
                                <div className="flex flex-col items-start py-2">
                                  <div className="font-bold capitalize">
                                    Groups
                                  </div>
                                  <DataTable
                                    data={groups}
                                    sort={false}
                                    // pagination={false}
                                    colFilter={false}
                                    cols={groupCols}
                                    search={false}
                                    rowsPerPage={4}
                                    totalCount={groups.length}
                                    className="mt-4 [&_td]:text-sm"
                                  />
                                </div>
                              </TabsContent>
                            )}
                            {_duplicateIds?.length > 0 && (
                              <TabsContent value="duplicates">
                                {duplicateData && (
                                  <DataTable
                                    data={duplicateFiles || []}
                                    sort={false}
                                    // pagination={false}
                                    colFilter={false}
                                    cols={duplicateDataCols}
                                    search={false}
                                    rowsPerPage={4}
                                    className="mt-4 [&_td]:text-sm"
                                    // rowClickColumn="id"
                                    totalCount={(duplicateFiles || []).length}
                                    onRowClick={
                                      handleDuplicateClick
                                      // (row) => {
                                      // setId(row);
                                      // setType("file");
                                      // setSource("file");
                                      // }
                                    }
                                  />
                                )}
                              </TabsContent>
                            )}
                            <TabsContent value="external_classification">
                              <DataTable
                                data={externalClassification}
                                cols={[
                                  { header: "Source", accessor: "source" },
                                  {
                                    header: "Classification",
                                    accessor: "classification",
                                  },
                                  {
                                    header: "LS Classification",
                                    accessor: "lsClassification",
                                  },
                                ]}
                                sort={false}
                                colFilter={false}
                                search={false}
                                rowsPerPage={4}
                                totalCount={externalClassification.length}
                              />
                            </TabsContent>
                          </Tabs>
                        )}
                      </div>
                    </div>
                  )}
                  {type == "email" && (
                    <div className="mt-4 grid grid-cols-6 gap-x-20">
                      <div className="col-span-2">
                        <Table className="border">
                          <TableBody>
                            {email_Data &&
                              Object.entries(email_Data).map(([key, value]) => {
                                return (
                                  <TableRow key={key}>
                                    <TableCell className="p-3">
                                      <span className="font-bold capitalize">
                                        {key.replace("_", " ")}
                                      </span>
                                    </TableCell>
                                    <TableCell className="p-3">
                                      <span className="block h-[20px] max-h-[50px] overflow-y-auto overflow-x-hidden">
                                        {value as string}
                                      </span>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                          </TableBody>
                        </Table>
                      </div>
                      <div className="col-span-4">
                        {email_Data && (
                          <div className="flex flex-col items-start py-2">
                            <div className="font-bold capitalize">
                              Attachments
                            </div>
                            {attachmentData && (
                              <DataTable
                                data={attachmentData}
                                sort={false}
                                // pagination={false}
                                colFilter={false}
                                cols={attachmentCols}
                                search={false}
                                totalCount={attachmentData.length}
                                rowsPerPage={4}
                                className="mt-4 [&_td]:text-sm"
                                rowClickColumn="attachmentId"
                                onRowClick={(row) => {
                                  setId(row);
                                  setType("file");
                                  setSource("email");
                                }}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {type == "database" && (
                    <div className="mt-4 grid grid-cols-6 gap-x-20">
                      <div className="col-span-2">
                        <Table className="border">
                          <TableBody>
                            {tableDetails &&
                              Object.entries(tableDetails).map(
                                ([key, value]) => {
                                  if (
                                    key == "users" ||
                                    key == "path" ||
                                    key == "size"
                                  )
                                    return;
                                  if (
                                    [
                                      "inserted_at",
                                      "modified_at",
                                      "created_at",
                                      "accessed_at",
                                    ].includes(key)
                                  )
                                    value = tableDetails[
                                      key as keyof typeof tableDetails
                                    ]
                                      ? new Date(
                                          Number(
                                            tableDetails[
                                              key as keyof typeof tableDetails
                                            ],
                                          ) * 1000,
                                        ).toLocaleString()
                                      : "--";
                                  // if (key == "size")
                                  //   value = formatBandwidth(
                                  //     tableDetails[
                                  //       key as keyof typeof fileData
                                  //     ] as number,
                                  //     "B",
                                  //     1,
                                  //   );
                                  return (
                                    <TableRow key={key}>
                                      <TableCell className="p-3">
                                        <span className="font-bold capitalize">
                                          {key.replace("_", " ")}
                                        </span>
                                      </TableCell>
                                      <TableCell className="p-3">
                                        <span>{value as string}</span>
                                      </TableCell>
                                    </TableRow>
                                  );
                                },
                              )}
                          </TableBody>
                        </Table>
                      </div>
                      <div className="col-span-4">
                        {tableDetails && (
                          <div className="flex flex-col items-start py-2">
                            <div className="font-bold capitalize">
                              User Access
                            </div>
                            {dbUserData && (
                              <DataTable
                                data={dbUserData}
                                sort={false}
                                // pagination={false}
                                colFilter={false}
                                cols={dbUserCols}
                                search={false}
                                rowsPerPage={4}
                                totalCount={dbUserData.length}
                                className="mt-4 [&_td]:text-sm"
                                // rowClickColumn="username"
                                onRowClick={(e) => {
                                  setUser(e.username);
                                  setPluginId(e.plugin_id ?? "");
                                }}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {type == "conversation" && (
                    <div className="mt-4 grid grid-cols-6 gap-x-20">
                      <div className="col-span-2">
                        <Table className="border">
                          <TableBody>
                            {conversationDetails &&
                              Object.entries(conversationDetails).map(
                                ([key, value]) => {
                                  if (key == "subject") return;
                                  return (
                                    <TableRow key={key}>
                                      <TableCell className="p-3">
                                        <span className="font-bold capitalize">
                                          {key.replace("_", " ")}
                                        </span>
                                      </TableCell>
                                      <TableCell className="p-3">
                                        <span>{value as string}</span>
                                      </TableCell>
                                    </TableRow>
                                  );
                                },
                              )}
                          </TableBody>
                        </Table>
                      </div>
                      <div className="col-span-4">
                        {conversationData && (
                          <div className="flex flex-col items-start py-2">
                            <div className="font-bold capitalize">
                              Attachments
                            </div>
                            {_attachmentData && (
                              <DataTable
                                data={_attachmentData}
                                sort={false}
                                // pagination={false}
                                colFilter={false}
                                cols={ConversationAttachmentCols}
                                search={false}
                                rowsPerPage={4}
                                totalCount={_attachmentData.length}
                                className="mt-4 [&_td]:text-sm"
                                rowClickColumn="attachmentId"
                                onRowClick={(row) => {
                                  setId(row);
                                  setType("file");
                                  setSource("conversation");
                                }}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {type == "generic" && (
                    <div className="mt-4 grid grid-cols-6 gap-x-20">
                      <div className="col-span-2">
                        <Table className="border">
                          <TableBody>
                            {genericDetails &&
                              Object.entries(genericDetails).map(
                                ([key, value]) => {
                                  return (
                                    <TableRow key={key}>
                                      <TableCell className="p-3">
                                        <span className="font-bold capitalize flex w-max">
                                          {key.replace(/_/g, " ")}
                                        </span>
                                      </TableCell>
                                      <TableCell className="p-3">
                                        <span>{value as string}</span>
                                      </TableCell>
                                    </TableRow>
                                  );
                                },
                              )}
                          </TableBody>
                        </Table>
                      </div>
                      <div className="col-span-4">
                        {genericDetails && (
                          <div className="flex flex-col items-start py-2">
                            <div className="font-bold capitalize">
                              Attachments
                            </div>
                            {genericAttachmentData && (
                              <DataTable
                                data={genericAttachmentData}
                                sort={false}
                                // pagination={false}
                                colFilter={false}
                                cols={genericAttachmentCols}
                                search={false}
                                rowsPerPage={4}
                                className="mt-4 [&_td]:text-sm"
                                rowClickColumn="attachmentId"
                                onRowClick={(row) => {
                                  setId(row);
                                  setType("file");
                                  setSource("generic");
                                  setDuplicateIds([]);
                                }}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <Separator className="my-2 h-1 bg-primary rounded-full" />
                </div>
              </>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default FileInvestigator;
